import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Edit2, Loader2, Plus, Trash2, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { Consumable } from '../types/consumable';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

export function ConsumablesPage() {
  const { get, post, patch } = useApi();
  const [items, setItems] = useState<Consumable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Add/Edit form
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formUnit, setFormUnit] = useState('each');
  const [formPrice, setFormPrice] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Deactivate
  const [deactivateId, setDeactivateId] = useState<string | null>(null);
  const deactivateItem = items.find(i => i.id === deactivateId);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = showInactive ? '?includeInactive=true' : '';
      const result = await get<Consumable[]>(`/consumables${params}`);
      if (result.success && result.data) setItems(result.data);
      else setError(result.error || 'Failed to load');
    } catch { setError('Failed to load consumables'); }
    finally { setLoading(false); }
  }, [get, showInactive]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = useCallback(() => {
    setEditId(null);
    setFormName('');
    setFormUnit('each');
    setFormPrice('');
    setFormCategory('');
    setShowForm(true);
  }, []);

  const openEdit = useCallback((item: Consumable) => {
    setEditId(item.id);
    setFormName(item.name);
    setFormUnit(item.unit);
    setFormPrice(item.unitPrice.toString());
    setFormCategory(item.category);
    setShowForm(true);
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const price = parseFloat(formPrice);
    if (!formName.trim() || isNaN(price) || price <= 0) {
      setError('Name and a valid price are required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      if (editId) {
        await patch(`/consumables/${editId}`, {
          name: formName.trim(), unit: formUnit, unitPrice: price, category: formCategory.trim(),
        });
      } else {
        await post('/consumables', {
          name: formName.trim(), unit: formUnit, unitPrice: price, category: formCategory.trim(),
        });
      }
      setShowForm(false);
      fetchItems();
    } catch { setError('Failed to save consumable'); }
    finally { setSubmitting(false); }
  }, [editId, formName, formUnit, formPrice, formCategory, post, patch, fetchItems]);

  const handleDeactivate = useCallback(async () => {
    if (!deactivateId) return;
    try {
      await patch(`/consumables/${deactivateId}`, { active: false });
      setDeactivateId(null);
      fetchItems();
    } catch { setError('Failed to deactivate'); }
  }, [deactivateId, patch, fetchItems]);

  const handleReactivate = useCallback(async (id: string) => {
    try {
      await patch(`/consumables/${id}`, { active: true });
      fetchItems();
    } catch { setError('Failed to reactivate'); }
  }, [patch, fetchItems]);

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">Consumables Registry</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded" />
            Show inactive
          </label>
          <button onClick={openAdd}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Add Consumable
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No consumables registered yet.</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map(item => (
                <tr key={item.id} className={!item.active ? 'opacity-60' : ''}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{item.unit}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{item.category || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">R {item.unitPrice.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    {item.active ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">Inactive</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {item.active ? (
                        <button onClick={() => setDeactivateId(item.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Deactivate">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : (
                        <button onClick={() => handleReactivate(item.id)}
                          className="px-2 py-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100">
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">{editId ? 'Edit Consumable' : 'Add Consumable'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={formName} onChange={e => setFormName(e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Suture — Vicryl 2-0" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select value={formUnit} onChange={e => setFormUnit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="each">Each</option>
                    <option value="pack">Pack</option>
                    <option value="ml">ml</option>
                    <option value="box">Box</option>
                    <option value="pair">Pair</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (R) *</label>
                  <input type="number" step="0.01" min="0.01" value={formPrice} onChange={e => setFormPrice(e.target.value)} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input type="text" value={formCategory} onChange={e => setFormCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Sutures, Dressings" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : editId ? 'Save Changes' : 'Add Consumable'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate confirm */}
      {deactivateId && deactivateItem && (
        <DeleteConfirmModal
          title="Deactivate Consumable"
          description={`Deactivate "${deactivateItem.name}"? It will be hidden from selection but can be reactivated later.`}
          onConfirm={handleDeactivate}
          onCancel={() => setDeactivateId(null)}
        />
      )}
    </main>
  );
}
